import React, { lazy, Suspense } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { popModal } from './globalModalsSlice';
import { useBooleanFeature } from '../../hooks/useFeatures';
import { useUserTags } from '../../hooks/useTags';
import InviteLinkDialog from '../team/invite/inviteByLinkModal';
import CreateOrUpdateFolder from '../library/folders/CreateOrUpdateFolderDialog';
import DeleteFilesModal from '../selectedVideo/actions/modals/DeleteFileModal';
import DeleteFolderModal from '../library/folders/actions/DeleteFolderModal';
import MoveFileModal from '../selectedVideo/actions/modals/MoveFileModal';
import UploadModal from '../upload/UploadModal';
import ImportModal from '../import/ImportModal';
import EmbedModal from '../selectedVideo/actions/modals/Embed';
import { RootState } from '../../reducers';
import UpgradeSuccessDialog from '../plan/upgrade/UpgradeSuccessDialog';
import UpSellModal from '../dashboard/toolbar/UpSellModal';
import ConnectGoogleCalendarModal from '../settings/meetbot/google/ConnectGoogleCalendarModal';
import FreeTrial from '../plan/upgrade/FreeTrial';
import LoaderComponent from '../app/loader/LoaderReact';
import ConnectMicrosoftTeamsCalendarModal from '../settings/meetbot/microsoft/ConnectMicrosoftTeamsCalendarModal';
import ConnectZoomMeetingsModal from '../settings/meetbot/zoom/ConnectZoomMeetingsModal';
import RecoveryFailureDialog from '../settings/RecoveryFailureDialog';
import RenameFileModal from '../selectedVideo/actions/modals/RenameFileModal';
import SignupOnboarding from '../plan/upgrade/SignupOnboarding';
import { OnboardingSurvey } from '../onboarding';

// Lazy load upgrade modal with retry logic for module loading failures
const LazyUpgradeModal = lazy(() =>
  import('../plan/upgrade/UpgradeModal').catch((error) => {
    console.error('Failed to load UpgradeModal, reloading page:', error);
    // If import fails, reload the page to get fresh modules
    window.location.reload();
    // Throw to prevent rendering with invalid module
    throw error;
  }),
);

const GlobalModalManager: React.FC = () => {
  const dispatch = useDispatch();
  const { getFeature } = useBooleanFeature();
  const { getTag } = useUserTags();
  const modalStack = useSelector(
    (state: RootState) => state.globalModals.modalStack,
  );
  const currentModal = modalStack[modalStack.length - 1];

  const onClose = () => {
    dispatch(popModal());
  };

  // Onboarding A/B test variants (mutually exclusive)
  // Group 1: Original OB flow (survey + trial modal)
  const isOriginalObVariant = getFeature('feature_show_signup_onboarding');
  // Group 3: OB survey + tooltip + upgrade modal on click
  const isObTooltipsVariant = getFeature('feature_ob_tooltips');
  // Group 4: OB survey + deferred upgrade modal on high-value action
  const isDeferredUpgradeVariant = getFeature('feature_deferred_upgrade_modal');

  // Check if user has completed onboarding survey
  const hasCompletedSurvey = getTag('onboardingStep') === 'completed';

  // Determine which modal to show for new users
  // For tooltip/deferred variants: show survey first if not completed
  const shouldShowSurveyFirst =
    (isObTooltipsVariant || isDeferredUpgradeVariant) && !hasCompletedSurvey;

  if (!currentModal) return null;

  const renderModal = () => {
    switch (currentModal) {
      case 'stripe-pricing-table-upgrade-modal':
        return (
          <Suspense fallback={<LoaderComponent />}>
            <LazyUpgradeModal onClose={onClose} />
          </Suspense>
        );
      case 'import-videos-modal':
        return <UploadModal onClose={onClose} />;
      case 'import-urls-modal':
        return <ImportModal onClose={onClose} />;
      case 'move-file-modal':
        return <MoveFileModal onClose={onClose} />;
      case 'delete-files-modal':
        return <DeleteFilesModal onClose={onClose} />;
      case 'invite-by-link-modal':
        return <InviteLinkDialog onClose={onClose} />;
      case 'create-folder-modal':
        return <CreateOrUpdateFolder onClose={onClose} />;
      case 'delete-folder-modal':
        return <DeleteFolderModal onClose={onClose} />;
      case 'embed-file-modal':
        return <EmbedModal onClose={onClose} />;
      case 'upgradeSuccess':
        return <UpgradeSuccessDialog onClose={onClose} />;
      case 'upsell-modal':
        return <UpSellModal onClose={onClose} />;
      case 'connect-google-calendar-modal':
        return <ConnectGoogleCalendarModal onClose={onClose} />;
      case 'free-trial-modal':
        // Onboarding A/B test with 4 mutually exclusive groups:
        // Group 1 (25%): Original OB flow - survey + trial modal
        // Group 2 (25%): Control - regular trial modal
        // Group 3 (25%): OB survey + tooltips - show survey first, then tooltips point to features
        // Group 4 (25%): OB survey + deferred - show survey first, then upgrade on high-value action

        // For tooltip/deferred variants: show survey-only first if not completed
        if (shouldShowSurveyFirst) {
          return (
            <OnboardingSurvey
              onClose={onClose}
              onComplete={() => {
                // Close modal after survey - tooltips will be shown by OnboardingContext
                // For deferred variant, user lands on dashboard
                onClose();
              }}
            />
          );
        }

        // For original OB variant: show full onboarding with trial
        if (isOriginalObVariant) {
          return <SignupOnboarding onClose={onClose} />;
        }

        // For control group and completed tooltip/deferred users: show regular trial modal
        return <FreeTrial onClose={onClose} />;
      case 'connect-microsoft-calendar-modal':
        return <ConnectMicrosoftTeamsCalendarModal onClose={onClose} />;
      case 'connect-zoom-meetings-modal':
        return <ConnectZoomMeetingsModal onClose={onClose} />;
      case 'recovery-failure-modal':
        return <RecoveryFailureDialog onClose={onClose} />;
      case 'rename-file-modal':
        return <RenameFileModal onClose={onClose} />;
      default:
        return null;
    }
  };

  return renderModal();
};

export default GlobalModalManager;
